import React, { useEffect } from "react";

import RaindropCircle from "../../../ui-components/raindrop-circle/raindrop-circle.tsx";
import RaindropButton, {
  ButtonStyle,
} from "../../../ui-components/raindrop-button/raindrop-button.tsx";

import { motion } from "framer-motion";
import RaindropMotionFadeUp from "../../../ui-components/raindrop-motion-fade-up/raindrop-motion-fade-up.tsx";
import { Size } from "../../../logic/enums.ts";

const Circle = ({ radius, strokeWidth, arcProportion }) => {
  if (arcProportion > 1) {
    arcProportion = 1;
  } else if (arcProportion < 0) {
    arcProportion = 0;
  }

  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - arcProportion);
  const strokeDasharray = `${circumference} ${circumference}`;
  const strokeDashoffset = offset;

  return (
    <motion.circle
      cx="50%"
      cy="50%"
      r={radius}
      fill="none"
      stroke={"white"}
      strokeWidth={strokeWidth}
      strokeLinecap={"round"}
      strokeDasharray={strokeDasharray}
      strokeDashoffset={strokeDashoffset}
      rotate={"45deg"}
      style={{
        transform: "rotate(-90deg)",
        transformOrigin: "50% 50%",
      }}
      className={"transition-all duration-1000 ease-linear"}
    />
  );
};

const PomodoroRing = ({ secondsTotal, secondsElapsed }) => {
  const progress = secondsElapsed / secondsTotal;
  const radius = 40;

  return (
    <RaindropMotionFadeUp className="absolute " delay={0.5}>
      <svg width="100%" height="100%" viewBox="0 0 100 100">
        <circle
          cx="50%"
          cy="50%"
          r={radius}
          fill="none"
          opacity={"20%"}
          stroke="white"
          strokeWidth={1}
        />

        <Circle radius={radius} strokeWidth={3} arcProportion={progress} />
      </svg>
    </RaindropMotionFadeUp>
  );
};

const PomodoroMainElement = ({ values, secondsElapsed, secondsTotal }) => {
  return (
    <div className="flex w-full flex-col justify-center items-center relative">
      <RaindropCircle
        sausageValues={values}
        animate={{ scale: 1 }}
        size={"SMALL"}
        isRaindropPlus={true}
        isPaused={false}
      />
      <PomodoroRing
        secondsElapsed={secondsElapsed}
        secondsTotal={secondsTotal}
      />
    </div>
  );
};

const PomodoroControlPanel = ({
  isPaused,
  isComplete,
  onStart,
  onPause,
  onReset,
  onRestart,
}) => {
  return (
    <div className="flex flex-col w-full p-2 gap-4 items-center ">
      {isComplete && (
        <RaindropButton
          onClick={onRestart}
          title={"Start Again"}
          size={Size.Large}
          style={ButtonStyle.Primary}
        />
      )}

      {isPaused && !isComplete && (
        <RaindropButton
          onClick={onStart}
          title={"Start"}
          size={Size.Large}
          style={ButtonStyle.Primary}
        />
      )}

      {!isPaused && (
        <RaindropButton onClick={onPause} title={"Pause"} size={Size.Large} />
      )}

      <RaindropButton
        onClick={onReset}
        title={"Reset"}
        style={ButtonStyle.Secondary}
      />
    </div>
  );
};

const RaindropPomodoro = ({ values }) => {
  const secondsTotal = 25 * 60;
  const [isPaused, setIsPaused] = React.useState(true);
  const [secondsElapsed, setSecondsElapsed] = React.useState(0);
  const [intervalId, setIntervalId] = React.useState(0);

  const isComplete = secondsElapsed >= secondsTotal;

  useEffect(() => {
    if (secondsElapsed >= secondsTotal) {
      setIsPaused(true);
      clearInterval(intervalId);
    }
  }, [secondsElapsed, secondsTotal, intervalId]);

  const onStart = () => {
    setIsPaused(false);
    const intervalId = setInterval(() => {
      setSecondsElapsed((prev) => prev + 1);
    }, 1000);
    setIntervalId(intervalId);
  };

  const onPause = () => {
    setIsPaused(true);
    clearInterval(intervalId);
  };

  const onReset = () => {
    setSecondsElapsed(0);
    setIsPaused(true);
    clearInterval(intervalId);
  };

  const onRestart = () => {
    onReset();
    onStart();
  };

  return (
    <div
      className={
        "flex flex-col justify-center  w-full h-full items-center relative gap-20 "
      }
    >
      <PomodoroMainElement
        values={values}
        secondsElapsed={secondsElapsed}
        secondsTotal={secondsTotal}
      />
      <PomodoroControlPanel
        isPaused={isPaused}
        isComplete={isComplete}
        onStart={onStart}
        onPause={onPause}
        onReset={onReset}
        onRestart={onRestart}
      />
    </div>
  );
};

export default RaindropPomodoro;
